import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Mail, Lock, Eye, EyeOff, AlertCircle, RefreshCw, Loader2, Fingerprint } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../api/client';
import { resendVerificationApi, checkBiometricAvailabilityApi } from '../api/auth';
import { getRememberedBiometricEmail, forgetBiometricEmail } from '../utils/biometric';
import { version as appVersion } from '../../package.json';
import { RequiredMark, FieldError, errBorder } from '../components/shared/FormField';
import { useFormErrors } from '../hooks/useFormErrors';

function InputField({ label, type, value, onChange, icon: Icon, placeholder, autoComplete, error, required }) {
  const [show, setShow] = useState(false);
  const isPassword = type === 'password';
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">{label}{required && <> <RequiredMark /></>}</label>
      <div className="relative flex items-center">
        <Icon size={15} className="absolute left-3.5 text-gray-400 pointer-events-none" />
        <input
          type={isPassword && show ? 'text' : type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className={`w-full pl-10 pr-10 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 placeholder:text-gray-400 transition-shadow ${errBorder(error)}`}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow((v) => !v)}
            className="absolute right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            {show ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        )}
      </div>
      <FieldError error={error} />
    </div>
  );
}

export default function Login() {
  const { t } = useTranslation();
  const { loginWithCredentials, loginWithBiometric, isBiometricSupported, isPlatformAuthenticatorAvailable, getBiometricErrorMessage } = useAuth();
  const navigate = useNavigate();
  // Email remembered from the last biometric sign-in on this device: pre-fill it and show the
  // biometric button straight away (re-checked with the server below).
  const [rememberedEmail, setRememberedEmail] = useState(() => getRememberedBiometricEmail());
  const [form, setForm] = useState({ email: rememberedEmail || '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [biometricSupported, setBiometricSupported] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [showBiometricButton, setShowBiometricButton] = useState(!!rememberedEmail);

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Check biometric support on mount
  useEffect(() => {
    const checkBiometricSupport = async () => {
      const supported = isBiometricSupported();
      setBiometricSupported(supported);
      if (supported) {
        const available = await isPlatformAuthenticatorAvailable();
        setBiometricAvailable(available);
      }
    };
    checkBiometricSupport();
  }, [isBiometricSupported, isPlatformAuthenticatorAvailable]);

  // Check if user has biometric credentials when email changes
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  useEffect(() => {
    const checkBiometricCredentials = async () => {
      if (form.email && biometricAvailable && isValidEmail(form.email)) {
        try {
          const { hasBiometricCredentials } = await checkBiometricAvailabilityApi(form.email);
          setShowBiometricButton(hasBiometricCredentials);
          if (form.email === rememberedEmail && !hasBiometricCredentials) {
            forgetBiometricEmail(); // device was removed; stop offering it
          }
        } catch (e) {
          // Network hiccup: keep the button for the remembered email, hide it otherwise
          setShowBiometricButton(form.email === rememberedEmail);
        }
      } else {
        // Keep the remembered email's button while platform support is still being detected;
        // the render condition below also requires biometricAvailable.
        if (!(rememberedEmail && form.email === rememberedEmail)) {
          setShowBiometricButton(false);
        }
      }
    };

    const timeout = setTimeout(checkBiometricCredentials, form.email === rememberedEmail ? 0 : 500);
    return () => clearTimeout(timeout);
  }, [form.email, biometricAvailable, rememberedEmail]);

  const set = (field) => (val) => setForm((f) => ({ ...f, [field]: val }));

  const handleResendVerification = async () => {
    setResendLoading(true);
    try {
      await resendVerificationApi(form.email);
      setCountdown(60);
      setError('');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setResendLoading(false);
    }
  };

  const { errors, validate } = useFormErrors(() => {
    const er = {};
    if (!form.email.trim()) er.email = t('validation.required', 'This field is required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) er.email = t('validation.invalidEmail', 'Please enter a valid email');
    if (!form.password) er.password = t('validation.required', 'This field is required');
    return er;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setEmailNotVerified(false);
    if (!validate()) return;
    setLoading(true);
    try {
      await loginWithCredentials(form.email, form.password);
      navigate('/', { replace: true });
    } catch (err) {
      const errMsg = getErrorMessage(err);
      // Check for email not verified error
      if (errMsg?.toLowerCase().includes('verify') || err?.response?.data?.error?.code === 'EMAIL_NOT_VERIFIED') {
        setEmailNotVerified(true);
      }
      setError(errMsg);
      setLoading(false);
    }
  };

  // Someone else is using this device: forget the remembered email and start with a blank form.
  const handleNotYou = () => {
    forgetBiometricEmail();
    setRememberedEmail(null);
    setShowBiometricButton(false);
    setError('');
    setForm({ email: '', password: '' });
  };

  const handleBiometricLogin = async () => {
    if (!form.email) {
      setError(t('auth.loginPage.pleaseEnterEmail'));
      return;
    }

    setBiometricLoading(true);
    setError('');
    setEmailNotVerified(false);

    try {
      await loginWithBiometric(form.email);
      navigate('/', { replace: true });
    } catch (err) {
      // Server errors (axios) carry their own message; browser/WebAuthn errors are translated locally
      const errMsg = err?.response ? getErrorMessage(err) : getBiometricErrorMessage(err);
      if (errMsg?.toLowerCase().includes('verify')) {
        setEmailNotVerified(true);
      }
      setError(errMsg);
      setBiometricLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-950 px-4 py-10">
      {/* Brand */}
      <div className="flex items-center justify-center gap-2.5 mb-8">
        <img src="/logo-login.svg" alt="FarmSmartly" className="h-14 w-auto" />
        <span className="text-3xl font-bold text-primary-800 dark:text-primary-300">FarmSmartly</span>
      </div>

      <div className="w-full max-w-sm bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 p-6">
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">{t('auth.loginPage.welcomeBack')}</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{t('auth.loginPage.signInToAccount')}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <InputField label={t('auth.loginPage.emailLabel')} type="email" value={form.email} onChange={set('email')}
            icon={Mail} placeholder={t('auth.loginPage.emailPlaceholder')} autoComplete="email" required error={errors.email} />
          {rememberedEmail && form.email === rememberedEmail && (
            <div className="-mt-2 text-right">
              <button
                type="button"
                onClick={handleNotYou}
                className="text-xs text-primary-700 dark:text-primary-400 hover:underline font-medium"
              >
                {t('auth.loginPage.notYou')}
              </button>
            </div>
          )}
          <div>
            <InputField label={t('auth.loginPage.passwordLabel')} type="password" value={form.password} onChange={set('password')}
              icon={Lock} placeholder={t('auth.loginPage.passwordPlaceholder')} autoComplete="current-password" required error={errors.password} />
            <div className="mt-1.5 text-right">
              <Link
                to="/forgot-password"
                className="text-xs text-primary-700 dark:text-primary-400 hover:underline font-medium"
              >
                {t('auth.loginPage.forgotPasswordLink')}
              </Link>
            </div>
          </div>

          {error && (
            <div className={`flex items-start gap-2 p-3 rounded-xl border text-sm ${emailNotVerified
                ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300'
                : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400'
              }`}>
              <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p>{error}</p>
                {emailNotVerified && (
                  <button
                    onClick={handleResendVerification}
                    disabled={resendLoading || countdown > 0}
                    className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-800 hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-medium transition-colors"
                  >
                    {resendLoading ? (
                      <><Loader2 size={12} className="animate-spin" /> {t('auth.loginPage.sending')}</>
                    ) : countdown > 0 ? (
                      <><RefreshCw size={12} /> {t('auth.loginPage.resendIn', { countdown })}</>
                    ) : (
                      <><RefreshCw size={12} /> {t('auth.loginPage.resendVerification')}</>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl bg-primary-800 hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors shadow-md"
          >
            {loading ? t('auth.loginPage.signingIn') : t('auth.loginPage.signInButton')}
          </button>

          {/* Biometric Login Button */}
          {showBiometricButton && biometricAvailable && (
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400">{t('auth.loginPage.or')}</span>
              </div>
            </div>
          )}

          {showBiometricButton && biometricAvailable && (
            <button
              type="button"
              onClick={handleBiometricLogin}
              disabled={biometricLoading}
              className="w-full py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60 disabled:cursor-not-allowed text-gray-700 dark:text-gray-200 font-semibold text-sm transition-colors flex items-center justify-center gap-2"
            >
              {biometricLoading ? (
                <><Loader2 size={16} className="animate-spin" /> {t('auth.loginPage.verifying')}</>
              ) : (
                <><Fingerprint size={16} /> {t('auth.loginPage.signInWithBiometric')}</>
              )}
            </button>
          )}

          {/* Biometric Info (when not available but email is entered) */}
          {form.email && !showBiometricButton && biometricAvailable && (
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              {t('auth.loginPage.enableBiometricHint')}
            </p>
          )}
        </form>

        <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
          {t('auth.loginPage.noAccount')}{' '}
          <Link to="/register" className="font-semibold text-primary-700 dark:text-primary-400 hover:underline">
            {t('auth.loginPage.registerFarm')}
          </Link>
        </div>
      </div>

      {import.meta.env.DEV && (
        <p className="mt-4 text-xs text-gray-400 text-center">
          API: <span className="font-mono text-gray-500">{import.meta.env.VITE_API_URL || 'http://localhost:8081/api/farm-smartly/v1'}</span>
        </p>
      )}

      {/* Copyright */}
      <p className="mt-4 text-xs text-gray-400 dark:text-gray-600 text-center">
        &copy; {new Date().getFullYear()} S2V Technology. All rights reserved.{' '}
        <Link to="/privacy" className="hover:underline">Privacy Policy</Link>
      </p>
      <p className="mt-1 text-[10px] text-gray-300 dark:text-gray-700 text-center">
        v{appVersion}
      </p>
    </div>
  );
}
